import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Calendar } from 'lucide-react';

export const CTASection = () => {
  return (
    <section className="relative py-24 md:py-32 overflow-hidden" data-testid="cta-section">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-navy via-deep-blue to-navy" />
      
      {/* Decorative Elements */}
      <div className="absolute inset-0">
        <motion.div
          animate={{
            y: [0, -20, 0],
            rotate: [0, 5, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="absolute top-10 left-10 w-72 h-72 bg-soft-gold/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            y: [0, 30, 0],
            rotate: [0, -5, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="absolute bottom-10 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl"
        />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-soft-gold/5 to-transparent rounded-full" />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Overline */}
          <span className="inline-block text-soft-gold text-xs sm:text-sm font-semibold uppercase tracking-[0.15em] sm:tracking-[0.2em] mb-6">
            Start Your Journey With Us
          </span>

          {/* Headline */}
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
            Let Heritage Home Health LLC Care for{' '}
            <span className="text-soft-gold">Your Loved Ones</span>
          </h2>

          {/* Description */}
          <p className="text-base sm:text-lg md:text-xl text-white/70 max-w-2xl mx-auto leading-relaxed mb-10">
            Take the first step towards exceptional in-home care. Our compassionate team 
            is ready to create a personalized care plan that brings comfort, dignity, 
            and peace of mind to your family.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="cta-contact-button"
              className="group inline-flex items-center justify-center space-x-3 bg-soft-gold text-navy px-6 sm:px-10 py-4 sm:py-5 rounded-full text-base sm:text-lg font-semibold hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 w-full sm:w-auto"
            >
              <Calendar className="w-5 h-5" />
              <span>Schedule Free Consultation</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="tel:+16083589578"
              data-testid="cta-phone-button"
              className="group inline-flex items-center justify-center space-x-3 bg-transparent text-white px-6 sm:px-10 py-4 sm:py-5 rounded-full text-base sm:text-lg font-semibold border-2 border-white/30 hover:border-white hover:bg-white/10 transition-all duration-300 w-full sm:w-auto"
            >
              <Phone className="w-5 h-5" />
              <span>+1 (608) 358-9578</span>
            </a>
          </div>

          {/* Trust Note */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-10 text-white/50 text-sm"
          >
            Free consultation • No obligation • Serving families for 15+ years
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;
